import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './lib/fonts'
import './lib/i18n'
import './index.css'
import App from './App.tsx'
import { ErrorScreen } from './components/ErrorScreen.tsx'
import { initMixpanel } from './lib/mixpanel'
import { track, AnalyticsEvent } from './lib/analytics'
import { initSentry } from './lib/sentry'
import { initDB, queuedAttachmentBytes } from './lib/db'
import { getContentStore, getSyncDevice } from './lib/services'
import { setLocalAttachmentFallback } from './lib/localAttachmentCache'
import { loadShortcutOverrides } from './store/shortcuts'
import { markLegacyDeviceIfEligible } from './lib/noteLimit'
import { captureDiscountCodeFromUrl } from './lib/purchases'
import { whatsNewAtLaunch } from './lib/whatsNew'
import * as Sentry from '@sentry/react'

// Telemetry must never block app startup (network may be unavailable offline)
try {
  initSentry()
} catch (e) {
  console.warn('[startup] Sentry init failed (offline?):', e)
}

try {
  initMixpanel()
  // Mirrors Swift's `CaliuApp.init()` → `app_launched`. No-op without a token.
  track(AnalyticsEvent.AppLaunched)
  // `pwa_installed` is web-only and has no other signal: the browser fires
  // `appinstalled` once, when the user accepts the install prompt.
  window.addEventListener('appinstalled', () => {
    track(AnalyticsEvent.PwaInstalled)
  })
} catch (e) {
  console.warn('[startup] Mixpanel init failed (offline?):', e)
}

// A page still running an older build lazy-loads a chunk the current deploy
// no longer serves (a page the service worker does not control fetches it
// from the network). Nothing on that page can recover, so reload onto the
// current build; unsaved edits are flushed on `beforeunload` (lib/sqlite.ts).
// At most once a minute, so a chunk missing from the current build too
// cannot loop: that failure then reaches Sentry like any other.
const STALE_CHUNK_RELOAD_KEY = 'caliu-stale-chunk-reload'
window.addEventListener('vite:preloadError', (event) => {
  try {
    const last = Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) ?? 0)
    if (Date.now() - last < 60_000) return
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()))
  } catch {
    // Storage blocked: no loop guard, so let the error through instead.
    return
  }
  event.preventDefault()
  window.location.reload()
})

// Before the router runs: its redirects (`/` → `/app`, auth) drop the query.
captureDiscountCodeFromUrl()

// "What's new": decide once per launch, before anything (onboarding) can set
// the completion flag it reads. `WhatsNewGate` presents it later.
whatsNewAtLaunch()

// Claim this tab's sync client id now: a duplicated tab (its sessionStorage
// copied) hears that the id is taken before it sends anything.
getSyncDevice()

// Initialize SQLite WASM, then render the app.
void (async () => {
  try {
    await initDB()
  } catch (e) {
    console.error('[startup] Failed to initialize database:', e)
    // Render error screen instead of leaving a blank page
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <ErrorScreen
          error={e instanceof Error ? e : new Error(String(e))}
          resetError={() => window.location.reload()}
        />
      </StrictMode>,
    )
    return
  }

  // Before any sync: a change in flight the database export missed is put
  // back, so it is resent under the same seq (lib/inflightRecords.ts).
  try {
    await getContentStore().reconcileInflight()
  } catch (e) {
    Sentry.captureException(e, { tags: { operation: 'inflight_reconcile' } })
  }

  // Load persisted shortcut overrides now that DB is ready
  loadShortcutOverrides()

  // Free note limit: before NOTE_LIMIT_CUTOFF, stamp a device that already
  // writes notes (or entered local mode) as legacy so it keeps unlimited local
  // notes after the cutoff. Must run every launch, and only once the DB exists.
  markLegacyDeviceIfEligible()

  // The blob cache is in memory and dies with the page, but the upload queue
  // is durable and, with no account, holds the ONLY copy of an attachment.
  // Wiring the resolver here (once the DB exists) is what makes a local-mode
  // image still render after a reload instead of 404ing on a URL nothing has
  // uploaded yet.
  setLocalAttachmentFallback(queuedAttachmentBytes)

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={({ error, resetError }: { error: unknown; resetError: () => void }) => <ErrorScreen error={error} resetError={resetError} />}>
        <App />
      </Sentry.ErrorBoundary>
    </StrictMode>,
  )
})()
